import { formatCurrency, formatCurrencyCompact, formatPercent } from '@obras/shared';
import { animate, useMotionValue, useMotionValueEvent, useReducedMotion } from 'motion/react';
import { useEffect, useLayoutEffect, useRef } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

/** Como escrever o número a cada quadro. Por nome, e não por função: a lista é fechada e estável. */
const FORMATOS = {
  inteiro: (valor: number) => Math.round(valor).toLocaleString('pt-BR'),
  /** Centavos, na forma curta dos indicadores: `R$ 48,6 mi`. */
  moeda: (valor: number) => formatCurrencyCompact(Math.round(valor)),
  /** Fração em [0, 1]: `43,8%`. */
  porcentagem: (valor: number) => formatPercent(valor),
} as const;

export type FormatoDoNumero = keyof typeof FORMATOS;

/** A espera antes de subir: a mesma do anel, para os dois chegarem juntos ao valor. */
const ATRASO = 0.15;

/**
 * O número grande que sobe até o valor ao entrar (design.md §10).
 *
 * Conta de zero até o valor em 500ms, na curva de saída: corre no começo e
 * assenta no fim, que é onde o olho lê. Valor novo, numa revalidação, corre
 * do anterior até ele. Escreve direto no DOM a cada quadro — estado de React
 * por quadro seria desperdício, como na caixa de mola.
 *
 * A largura é a do valor final, guardada por uma cópia invisível dele: o
 * número não empurra o que está ao lado enquanto sobe. O leitor de tela lê só
 * o valor final; em moeda, a dica traz o valor por extenso. Com movimento
 * reduzido, aparece pronto.
 */
export function NumeroQueSobe({
  valor,
  formato,
  className,
}: {
  valor: number;
  formato: FormatoDoNumero;
  className?: string | undefined;
}) {
  const reduzir = useReducedMotion();
  const atual = useMotionValue(reduzir ? valor : 0);
  const ref = useRef<HTMLSpanElement>(null);
  const formatar = FORMATOS[formato];
  const final = formatar(valor);

  const escrever = (numero: number) => {
    if (ref.current) ref.current.textContent = formatar(numero);
  };
  useMotionValueEvent(atual, 'change', escrever);
  useLayoutEffect(() => {
    escrever(atual.get());
  });

  useEffect(() => {
    if (reduzir) {
      atual.jump(valor);
      return undefined;
    }
    const controle = animate(atual, valor, { duration: 0.5, ease: EASE_OUT_QUINT, delay: ATRASO });
    return () => controle.stop();
  }, [valor, reduzir, atual]);

  return (
    <span
      className={cn('relative inline-block whitespace-nowrap', className)}
      title={formato === 'moeda' ? formatCurrency(Math.round(valor)) : undefined}
    >
      <span className="invisible" aria-hidden>
        {final}
      </span>
      <span ref={ref} className="absolute inset-0" aria-hidden />
      <span className="sr-only">{final}</span>
    </span>
  );
}
